import React, { useState } from 'react';

/**
 * Reusable failure-with-retry block. Mirrors the iOS LoadFailureView so the
 * "couldn't reach the server, try again" experience is consistent across
 * platforms.
 *
 * Props:
 *   - title:       headline (default "Couldn't load")
 *   - message:     optional supporting line — friendly NSURLError-style copy
 *   - onRetry:     async () => void — disables button while in-flight
 *   - icon:        Font Awesome class (default "fa-wifi-slash")
 *   - fullscreen:  centers vertically + drops the surface card. Use when the
 *                  failure replaces an entire page (e.g. people load).
 */
export default function LoadFailure({
  title = "Couldn't load",
  message,
  onRetry,
  icon = 'fa-wifi-exclamation',
  fullscreen = false,
}) {
  const [retrying, setRetrying] = useState(false);

  async function handleRetry() {
    if (retrying) return;
    setRetrying(true);
    try { await onRetry?.(); } finally { setRetrying(false); }
  }

  const wrapStyle = fullscreen
    ? { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        gap:14, padding:'80px 20px', textAlign:'center', minHeight:300 }
    : { display:'flex', flexDirection:'column', alignItems:'center', gap:14,
        padding:'28px 20px', textAlign:'center',
        background:'var(--card)', borderRadius:12, border:'1px solid var(--border)' };

  return (
    <div style={wrapStyle}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: fullscreen ? 36 : 26, color:'var(--text3)' }} />
      <div>
        <p style={{ fontSize: fullscreen ? 17 : 15, fontWeight:600, color:'var(--text)', margin:'0 0 4px' }}>{title}</p>
        {message && (
          <p style={{ fontSize:13, color:'var(--text2)', margin:0, maxWidth:340 }}>{message}</p>
        )}
      </div>
      <button
        onClick={handleRetry}
        disabled={retrying}
        style={{
          display:'inline-flex', alignItems:'center', gap:6,
          padding:'8px 14px', borderRadius:999,
          background:'var(--accent-soft, rgba(99,102,241,0.12))',
          color:'var(--accent, #6366f1)',
          border:'none', cursor: retrying ? 'default' : 'pointer',
          fontSize:14, fontWeight:600, fontFamily:'inherit',
          opacity: retrying ? 0.6 : 1,
        }}>
        <i className={`fa-solid ${retrying ? 'fa-spinner fa-spin' : 'fa-arrow-rotate-right'}`} style={{ fontSize:13 }} />
        {retrying ? 'Retrying…' : 'Try again'}
      </button>
    </div>
  );
}
